/**
 * MapLegend — compact legend for visible layers.
 */

import React from 'react';
import type { LayerVisibility } from '../types/mapTypes';

interface MapLegendProps {
  visibility: LayerVisibility;
}

interface LegendItem {
  label: string;
  color: string;
  shape: 'polygon' | 'line' | 'circle' | 'dash';
  visible: boolean;
}

export default function MapLegend({ visibility }: MapLegendProps) {
  const items: LegendItem[] = [
    { label: 'Farm Field', color: '#3B82F6', shape: 'polygon' as const, visible: visibility.fields },
    { label: 'Tree (healthy)', color: '#16A34A', shape: 'circle' as const, visible: visibility.trees },
    { label: 'Tree (issue)', color: '#DC2626', shape: 'circle' as const, visible: visibility.trees },
    { label: 'Farmer Plot', color: '#10B981', shape: 'polygon' as const, visible: visibility.farmers },
    { label: 'Cohort', color: '#2563EB', shape: 'line' as const, visible: visibility.cohorts },
    { label: 'Warehouse', color: '#7C3AED', shape: 'circle' as const, visible: visibility.warehouses },
  ].filter((item) => item.visible);

  if (items.length === 0) return null;

  return (
    <div className="bg-white/95 rounded-lg shadow-md border border-slate-200 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
        Legend
      </p>
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-2 text-xs text-slate-600">
            {item.shape === 'circle' && (
              <span
                className="w-3 h-3 rounded-full shrink-0 border border-white shadow-sm"
                style={{ backgroundColor: item.color }}
              />
            )}
            {item.shape === 'polygon' && (
              <span
                className="w-3 h-3 rounded-sm shrink-0 border"
                style={{
                  backgroundColor: `${item.color}33`,
                  borderColor: item.color,
                }}
              />
            )}
            {item.shape === 'line' && (
              <span
                className="w-4 h-0 shrink-0 border-t-2"
                style={{ borderColor: item.color }}
              />
            )}
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
